// App.tsx
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './components/common/Toast/ToastProvider';

// Layouts
import MainLayout from './layouts/MainLayout/MainLayout';
import DashboardLayout from './layouts/DashboardLayout/DashboardLayout';

// Main Page Views
import HomePage from './views/MainPage/HomePage/HomePage';
import LoginPage from './views/MainPage/LoginPage/LoginPage';
import RegisterPage from './views/MainPage/RegisterPage/RegisterPage';
import ForgotPasswordPage from './views/MainPage/ForgotPasswordPage/ForgotPasswordPage';
import ReferralPage from './views/MainPage/ReferralPage/ReferralPage';
import InfoPage from './views/MainPage/InfoPage/InfoPage';

// Dashboard Views
import Overview from './views/Dashboard/Overview/Overview';
import Bonuses from './views/Dashboard/Bonuses/Bonuses';
import BonusHistory from './views/Dashboard/BonusHistory/BonusHistory';
import Referrals from './views/Dashboard/Referrals/Referrals';
import Settings from './views/Dashboard/Settings/Settings';
import Journey from './views/Dashboard/Journey/Journey';
import Screenia from './views/Dashboard/Screenia/Screenia';
import Investments from './views/Dashboard/Investments/Investments';

// Admin Views
import AdminBonuses from './views/Admin/Bonuses/AdminBonuses';
import AdminStatistics from './views/Admin/Statistics/AdminStatistics';
import PushNotifications from './views/Admin/PushNotifications/PushNotifications';
import Transactions from './views/Admin/Transactions/Transactions';

// Protected Route Component
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';

// Demo-only: view the dashboard without a backend/login (safe to remove)
import DashboardPreview from './views/_preview/DashboardPreview';

// Global styles
import './styles/globals.css';

function App() {
  const navigate = useNavigate();

  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider navigate={navigate}>
          <Routes>
            {/* Main Page Routes */}
            <Route path="/" element={<MainLayout />}>
              <Route index element={<HomePage />} />
              <Route path="login" element={<LoginPage />} />
              <Route path="referral" element={<ReferralPage />} />
              <Route path="register" element={<RegisterPage />} />
              <Route path="forgot-password" element={<ForgotPasswordPage />} />
              <Route path="info" element={<InfoPage />} />
            </Route>

            {/* User Dashboard Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Overview />} />
              <Route path="bonuses" element={<Bonuses />} />
              <Route path="bonus-history" element={<BonusHistory />} />
              <Route path="referrals" element={<Referrals />} />
              <Route path="investments" element={<Investments />} />
              <Route path="settings" element={<Settings />} />
              <Route path="journey" element={<Journey />} />
            </Route>

            {/* Admin Dashboard Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route path="bonuses" element={<AdminBonuses />} />
              <Route path="push_notifications" element={<PushNotifications />} />
              <Route path="statistics" element={<AdminStatistics />} />
              <Route path="transactions" element={<Transactions />} />
              <Route path="screenia" element={<Screenia />} />
            </Route>

            {/* Demo-only dashboard preview (no login/backend needed) */}
            <Route
              path="/preview/dashboard"
              element={
                <DashboardPreview>
                  <DashboardLayout />
                </DashboardPreview>
              }
            >
              <Route index element={<Overview />} />
              <Route path="bonuses" element={<Bonuses />} />
              <Route path="bonus-history" element={<BonusHistory />} />
              <Route path="referrals" element={<Referrals />} />
              <Route path="investments" element={<Investments />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
